import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";
import { ConfirmSheet } from "./ui.tsx";

let dirtyForms = 0;
let leaving = false;
/**
 * The person confirmed leaving (discarding edits): from now on the browser's own "leave this page?"
 * prompt must not appear a second time, e.g. when a new version reloads the page.
 */
export function disarmUnsavedGuards(): void {
  leaving = true;
  setTimeout(() => {
    leaving = false; // the reload did not happen after all: guard again
  }, 10_000);
}
/** True while any admin form has unsaved edits. */
export const hasUnsavedEdits = (): boolean => dirtyForms > 0;

/**
 * Unsaved edits are never lost silently: while `dirty`, leaving the page (a link, back/forward, a
 * language switch) asks first, and closing or reloading the tab triggers the browser's own prompt.
 * `allowLeave()` lets one deliberate navigation through (after a save or a delete).
 */
export function useUnsavedGuard(dirty: boolean) {
  const dirtyRef = useRef(dirty);
  const bypass = useRef(false);
  useEffect(() => {
    dirtyRef.current = dirty;
    if (!dirty) return;
    dirtyForms++; // lets the "new version" toast know a reload would lose something
    return () => {
      dirtyForms--;
    };
  }, [dirty]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirtyRef.current &&
      !bypass.current &&
      currentLocation.pathname + currentLocation.search !==
        nextLocation.pathname + nextLocation.search,
  );

  useEffect(() => {
    if (!dirty) return;
    const onUnload = (e: BeforeUnloadEvent) => {
      if (leaving) return;
      e.preventDefault();
      e.returnValue = ""; // legacy browsers need a value
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [dirty]);

  return {
    blocker,
    allowLeave: () => {
      bypass.current = true;
    },
  };
}

type Blocker = ReturnType<typeof useBlocker>;

/** The "discard changes?" sheet; shows while the router holds a blocked navigation. */
export function DiscardSheet({ blocker }: { blocker: Blocker }) {
  if (blocker.state !== "blocked") return null;
  return <DiscardConfirm onDiscard={() => blocker.proceed()} onKeep={() => blocker.reset()} />;
}

export function DiscardConfirm({
  onDiscard,
  onKeep,
  body,
}: {
  onDiscard: () => void;
  onKeep: () => void;
  /** Overrides the generic "your changes were not saved" text. */
  body?: string;
}) {
  const { t } = useTranslation();
  return (
    <ConfirmSheet
      title={t("admin.discard.title")}
      body={body ?? t("admin.discard.body")}
      confirmLabel={t("admin.discard.confirm")}
      cancelLabel={t("admin.discard.keep")}
      onConfirm={onDiscard}
      onCancel={onKeep}
    />
  );
}
